export interface SectionField {
  key: string;
  label: string;
  type?: 'text' | 'date';
  multiline?: boolean;
}

export interface SectionConfig {
  id: string;
  name: string;
  description: string;
  fields: SectionField[];
  hasPhotos: boolean;
}

export const INSTALLATION_SECTIONS: SectionConfig[] = [
  {
    id: 'siteInformation',
    name: 'Site Information',
    description: 'Contact and key dates for this installation. Name, address, and phone come from the hospital record.',
    hasPhotos: false,
    fields: [
      { key: 'administrator', label: 'Administrator' },
      { key: 'email', label: 'Administrator Email' },
      { key: 'installationStart', label: 'Installation Start', type: 'date' },
      { key: 'installationEnd', label: 'Installation End', type: 'date' },
      { key: 'postInstallation', label: 'Post-Installation', type: 'date' },
      { key: 'launchDate', label: 'Launching Date', type: 'date' },
    ],
  },
  {
    id: 'floorPlan',
    name: 'Floor Plan',
    description: 'Upload the designed floor plan as an image or PDF.',
    hasPhotos: true,
    fields: [{ key: 'notes', label: 'Notes', multiline: true }],
  },
  {
    id: 'ispRouter',
    name: 'ISP & Router',
    description: 'Record the internet service and router details, and add photos of the installed router.',
    hasPhotos: true,
    fields: [
      { key: 'provider', label: 'Provider' },
      { key: 'accountNumber', label: 'Account #' },
      { key: 'speed', label: 'Speed' },
      { key: 'monthlyCost', label: 'Monthly Cost' },
      { key: 'ssid', label: 'SSID' },
      { key: 'password', label: 'Password' },
      { key: 'ip', label: 'IP' },
      { key: 'supportContact', label: 'Support Contact' },
    ],
  },
  {
    id: 'switch',
    name: 'Switch',
    description: 'Add photos of the installed switch and record its details.',
    hasPhotos: true,
    fields: [
      { key: 'location', label: 'Location' },
      { key: 'model', label: 'Model' },
      { key: 'notes', label: 'Notes', multiline: true },
    ],
  },
  {
    id: 'accessPoints',
    name: 'Access Points',
    description: 'Record where the access points are installed and add photos of each.',
    hasPhotos: true,
    fields: [{ key: 'locations', label: 'Locations', multiline: true }],
  },
  {
    id: 'gateways',
    name: 'Gateways',
    description: 'Record where the gateways are installed and add photos of each.',
    hasPhotos: true,
    fields: [{ key: 'locations', label: 'Locations', multiline: true }],
  },
  {
    id: 'tvs',
    name: 'TVs',
    description: 'Record where the TVs are installed and add photos of each.',
    hasPhotos: true,
    fields: [{ key: 'locations', label: 'Locations', multiline: true }],
  },
  {
    id: 'ipads',
    name: 'iPads',
    description: 'Record where the iPads are assigned and add photos.',
    hasPhotos: true,
    fields: [{ key: 'assignments', label: 'Assignments', multiline: true }],
  },
  {
    id: 'temperatureHumidity',
    name: 'Temperature & Humidity',
    description: 'Record where the temperature and humidity sensors are installed and add photos.',
    hasPhotos: true,
    fields: [{ key: 'locations', label: 'Sensor Locations', multiline: true }],
  },
];

export const isSectionComplete = (
  section: SectionConfig,
  fields: Record<string, string> | undefined,
  photoCount: number,
): boolean =>
  section.fields.every(field => !!(fields?.[field.key] ?? '').trim()) && (!section.hasPhotos || photoCount > 0);

export const sectionFieldsWithoutPhotos = (section: Record<string, unknown> | undefined): Record<string, string> => {
  if (!section) return {};
  const next: Record<string, string> = {};
  Object.entries(section).forEach(([key, value]) => {
    if (key !== 'photos' && value != null) {
      next[key] = String(value);
    }
  });
  return next;
};

export const parseInstallationAnswers = (raw: unknown): Record<string, Record<string, unknown>> => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object') {
    return raw as Record<string, Record<string, unknown>>;
  }
  return {};
};
